import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, RefreshCw, Clock, UserCheck, Activity } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface ConnectedUser {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  company: string;
  avatar_url?: string;
  current_page: string;
  is_admin: boolean;
  seconds_ago: number;
}

interface PresenceResponse {
  total_connected: number;
  admin_count: number;
  user_count: number;
  users: ConnectedUser[];
}

export function ConnectedUsersCard() {
  const { profile } = useAuth();
  const apiClient = useApiClient();
  const [data, setData] = useState<PresenceResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Solo se visualiza para usuarios con rol 'admin'
  if (profile?.role !== "admin") {
    return null;
  }

  const fetchOnlineUsers = async () => {
    try {
      setLoading(true);
      const res = await (apiClient as any).get("/api/v1/admin/online-users");
      setData(res);
    } catch (err) {
      console.error("Error al obtener usuarios conectados:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnlineUsers();
    const timer = setInterval(fetchOnlineUsers, 15000); // Auto-refresco cada 15s
    return () => clearInterval(timer);
  }, []);

  const getRoleBadge = (role: string, isAdmin: boolean) => {
    if (isAdmin || role === "admin") {
      return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-300 font-semibold">Admin</Badge>;
    }
    if (role === "lawyer") {
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300 font-semibold">Abogado</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-200">{role || "Usuario"}</Badge>;
  };

  return (
    <Card className="rounded-2xl border-slate-200 shadow-sm overflow-hidden bg-white mb-6">
      <CardHeader className="border-b border-slate-100 bg-slate-50/70 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-50 rounded-xl border border-emerald-200 shadow-sm">
              <Users className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg font-bold text-slate-900">
                  Usuarios Conectados en Tiempo Real
                </CardTitle>
                <span className="flex h-2.5 w-2.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
              </div>
              <CardDescription className="text-xs text-slate-500">
                Monitoreo en vivo exclusivo para Administradores
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full font-bold text-sm shadow-sm">
              <UserCheck className="h-4 w-4 text-emerald-600" />
              <span>{data?.total_connected ?? 1} en línea</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchOnlineUsers}
              disabled={loading}
              className="h-8 w-8 p-0 rounded-lg border-slate-200 hover:bg-slate-100">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin text-blue-600" : "text-slate-600"}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-2">
        {!data?.users || data.users.length === 0 ? (
          <div className="text-center py-6">
            <Activity className="h-8 w-8 text-slate-300 mx-auto mb-2 animate-pulse" />
            <p className="text-sm font-medium text-slate-500">Cargando presencia de usuarios...</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.users.map((u) => (
              <div key={u.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between py-2.5 hover:bg-slate-50/80 px-2.5 rounded-xl transition-colors gap-2">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9 border border-slate-200 shadow-sm">
                    <AvatarImage src={u.avatar_url} />
                    <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white text-xs font-bold">
                      {u.full_name?.slice(0, 2).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-slate-900">{u.full_name}</span>
                      {getRoleBadge(u.role, u.is_admin)}
                    </div>
                    <p className="text-xs text-slate-500 font-medium">
                      {u.email} {u.company && u.company !== "N/A" ? `• ${u.company}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center text-xs text-slate-500">
                  <div className="font-semibold text-slate-700 flex items-center gap-1">
                    <Clock className="h-3 w-3 text-emerald-500" />
                    <span>hace {u.seconds_ago < 5 ? "unos segundos" : `${u.seconds_ago}s`}</span>
                  </div>
                  <span className="text-[11px] text-slate-400 font-mono">{u.current_page}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
